<template>
  <div class="vehicle-edit-page">
    <div class="page-header">
      <span class="page-title">{{ isEdit ? '无人驾驶车辆(装备)信息变更' : '无人驾驶车辆(装备)报备' }}</span>
      <a-button type="primary" @click="goBack">
        返回
        <template #icon><ArrowLeftOutlined /></template>
      </a-button>
    </div>

    <div class="page-body">
      <a-spin :spinning="submitLoading">
        <a-form
          ref="formRef"
          :model="formData"
          :rules="rules"
          layout="vertical"
          class="edit-form"
        >
          <!-- 基本信息 -->
          <div class="form-section">
            <div class="section-title">基本信息</div>
            <a-row :gutter="24">
              <a-col :span="12">
                <a-form-item label="车辆识别代码" name="vin">
                  <a-input
                    v-model:value="formData.vin"
                    placeholder="请输入车辆识别代码"
                    :maxlength="50"
                  />
                </a-form-item>
              </a-col>
              <a-col :span="12">
                <a-form-item label="车辆型号" name="model">
                  <a-input
                    v-model:value="formData.model"
                    placeholder="请输入车辆型号"
                    :maxlength="50"
                  />
                </a-form-item>
              </a-col>
              <a-col :span="12">
                <a-form-item label="类型" name="type">
                  <a-select
                    v-model:value="formData.type"
                    placeholder="请选择类型"
                    :options="vehicleTypeOptions"
                    :field-names="{ label: 'name', value: 'id' }"
                    show-search
                    :filter-option="(input, option) => (option.name || '').includes(input)"
                    allow-clear
                  />
                </a-form-item>
              </a-col>
              <a-col :span="12">
                <a-form-item label="生产企业" name="manufacturer">
                  <a-input
                    v-model:value="formData.manufacturer"
                    placeholder="请输入生产企业"
                    :maxlength="100"
                  />
                </a-form-item>
              </a-col>
              <a-col :span="12">
                <a-form-item label="生产日期" name="productionDate">
                  <a-date-picker
                    v-model:value="formData.productionDate"
                    format="YYYY-MM-DD"
                    value-format="YYYY-MM-DD"
                    style="width: 100%"
                    placeholder="请选择生产日期"
                  />
                </a-form-item>
              </a-col>
              <a-col :span="12">
                <a-form-item label="整机尺寸" name="overallSize">
                  <a-input
                    v-model:value="formData.overallSize"
                    placeholder="请输入整机尺寸"
                    :maxlength="100"
                  />
                </a-form-item>
              </a-col>
              <a-col :span="12">
                <a-form-item label="续航里程(km)" name="range">
                  <a-input-number
                    v-model:value="formData.range"
                    :min="0"
                    :precision="2"
                    placeholder="请输入续航里程"
                    style="width: 100%"
                  />
                </a-form-item>
              </a-col>
              <a-col :span="12">
                <a-form-item label="整备质量(kg)" name="curbWeight">
                  <a-input-number
                    v-model:value="formData.curbWeight"
                    :min="0"
                    :precision="2"
                    placeholder="请输入整备质量"
                    style="width: 100%"
                  />
                </a-form-item>
              </a-col>
              <a-col :span="12">
                <a-form-item label="载重(kg)" name="capacityLoad">
                  <a-input-number
                    v-model:value="formData.capacityLoad"
                    :min="0"
                    :precision="2"
                    placeholder="请输入载重"
                    style="width: 100%"
                  />
                </a-form-item>
              </a-col>
              <a-col :span="12">
                <a-form-item label="最大速度(km/h)" name="maxSpeed">
                  <a-input-number
                    v-model:value="formData.maxSpeed"
                    :min="0"
                    :precision="2"
                    placeholder="请输入最大速度"
                    style="width: 100%"
                  />
                </a-form-item>
              </a-col>
            </a-row>
          </div>

          <!-- 车辆合格证 -->
          <div class="form-section">
            <div class="section-title">车辆合格证</div>
            <a-form-item label="车辆合格证" name="certificateUrl">
              <ImageUploader
                v-model:imageUrl="formData.certificateUrl"
                pathName="vehicle/certificate"
                :boxWidth="320"
                :boxHeight="220"
                uploadText="上传车辆合格证"
              />
            </a-form-item>
          </div>

          <!-- 车辆保险信息 -->
          <div class="form-section">
            <div class="section-title">车辆保险信息</div>
            <a-row :gutter="24">
              <a-col :span="12">
                <a-form-item label="保险公司" name="insuranceCompany">
                  <a-input
                    v-model:value="formData.insuranceCompany"
                    placeholder="请输入保险公司"
                    :maxlength="100"
                  />
                </a-form-item>
              </a-col>
              <a-col :span="12">
                <a-form-item label="保险单号" name="insuranceNo">
                  <a-input
                    v-model:value="formData.insuranceNo"
                    placeholder="请输入保险单号"
                    :maxlength="100"
                  />
                </a-form-item>
              </a-col>
              <a-col :span="12">
                <a-form-item label="保险金额" name="insuranceAmount">
                  <a-input-number
                    v-model:value="formData.insuranceAmount"
                    :min="0"
                    :precision="2"
                    placeholder="请输入保险金额"
                    style="width: 100%"
                  />
                </a-form-item>
              </a-col>
              <a-col :span="12">
                <a-form-item label="保险起止日期" name="insuranceDateRange">
                  <a-range-picker
                    v-model:value="formData.insuranceDateRange"
                    format="YYYY-MM-DD"
                    value-format="YYYY-MM-DD"
                    style="width: 100%"
                    :placeholder="['开始日期', '结束日期']"
                  />
                </a-form-item>
              </a-col>
            </a-row>
            <a-form-item label="保险证明文件" name="insuranceFiles">
              <FileUpload
                ref="insuranceFileRef"
                pathName="vehicle/insurance"
                updateTitle="上传保险证明"
                :maxCount="10"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                @uploadFinished="handleInsuranceUpload"
                @fileListChange="handleInsuranceListChange"
              />
            </a-form-item>
          </div>

          <!-- 车辆测试报告 -->
          <div class="form-section">
            <div class="section-title">车辆测试报告</div>
            <a-form-item label="测试报告附件" name="testReportFiles">
              <FileUpload
                ref="testReportFileRef"
                pathName="vehicle/testReport"
                updateTitle="上传测试报告"
                :maxCount="10"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                @uploadFinished="handleTestReportUpload"
                @fileListChange="handleTestReportListChange"
              />
            </a-form-item>
          </div>

          <!-- 提交按钮 -->
          <div class="form-actions">
            <a-button type="primary" :loading="submitLoading" @click="handleSubmit">
              {{ isEdit ? '提交变更' : '提交备案' }}
            </a-button>
            <a-button class="ml16" @click="goBack">取消</a-button>
          </div>
        </a-form>
      </a-spin>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { message } from "ant-design-vue";
import { ArrowLeftOutlined } from "@ant-design/icons-vue";
import ImageUploader from "@/components/imageUploader/index.vue";
import FileUpload from "@/components/fileUpload/index.vue";
import useUserStore from "@/store/modules/user";
import { getVehicleTypeList } from "@/api/settings/vehicleType";
import { getVehicleInfo, addVehicle, updateVehicle } from "@/api/vehicle";

const route = useRoute();
const router = useRouter();
const store = useUserStore();
const formRef = ref();
const insuranceFileRef = ref();
const testReportFileRef = ref();
const submitLoading = ref(false);
const vehicleId = ref(route.query.id || "");
const isEdit = computed(() => !!vehicleId.value);

// 车辆类型列表
const vehicleTypeOptions = ref([]);
const loadVehicleTypeList = async () => {
  try {
    let params = {
      categoryType: 'autonomous_vehicle'
    };
    const res = await getVehicleTypeList(params);
    if (res.code === 200 && res.data) {
      vehicleTypeOptions.value = res.data;
    }
  } catch (error) {
    console.error("获取车辆类型列表失败:", error);
  }
};

// 表单数据
const formData = reactive({
  vin: "",
  model: "",
  type: undefined,
  manufacturer: "",
  productionDate: undefined,
  overallSize: "",
  range: undefined,
  curbWeight: undefined,
  capacityLoad: undefined,
  maxSpeed: undefined,
  certificateUrl: "",
  insuranceCompany: "",
  insuranceNo: "",
  insuranceAmount: undefined,
  insuranceDateRange: undefined,
  insuranceFiles: [],
  testReportFiles: [],
});

// 表单校验规则
const rules = {
  vin: [{ required: true, message: "请输入车辆识别代码", trigger: "blur" }],
  model: [{ required: true, message: "请输入车辆型号", trigger: "blur" }],
  type: [{ required: true, message: "请选择类型", trigger: "change" }],
  manufacturer: [{ required: true, message: "请输入生产企业", trigger: "blur" }],
  productionDate: [
    { required: true, message: "请选择生产日期", trigger: "change" },
  ],
  certificateUrl: [
    { required: true, message: "请上传车辆合格证", trigger: "change" },
  ],
};

// 保险证明上传完成回调
const handleInsuranceUpload = () => {};

// 保险证明文件列表变化回调
const handleInsuranceListChange = (fileList) => {
  formData.insuranceFiles = fileList
    .filter((f) => f.status === "done" && f.url)
    .map((f) => ({ fileName: f.name, fileUrl: f.url }));
};

// 测试报告上传完成回调
const handleTestReportUpload = () => {};

// 测试报告文件列表变化回调
const handleTestReportListChange = (fileList) => {
  formData.testReportFiles = fileList
    .filter((f) => f.status === "done" && f.url)
    .map((f) => ({ fileName: f.name, fileUrl: f.url }));
};

// 加载车辆详情回显
const loadVehicleDetail = async () => {
  if (!vehicleId.value) return;
  submitLoading.value = true;
  try {
    const res = await getVehicleInfo(vehicleId.value);
    if (res.code === 200 && res.data) {
      const data = res.data;
      // 基本信息
      formData.vin = data.vin || "";
      formData.model = data.model || "";
      formData.type = data.type ?? undefined;
      formData.manufacturer = data.manufacturer || "";
      formData.productionDate = data.productionDate || undefined;
      formData.overallSize = data.overallSize || "";
      formData.range = data.range ?? undefined;
      formData.curbWeight = data.curbWeight ?? undefined;
      formData.capacityLoad = data.capacityLoad ?? undefined;
      formData.maxSpeed = data.maxSpeed ?? undefined;
      // 车辆合格证图片
      formData.certificateUrl = data.certificateUrl || "";
      // 车辆保险信息
      formData.insuranceCompany = data.insuranceCompany || "";
      formData.insuranceNo = data.insuranceNo || "";
      formData.insuranceAmount = data.insuranceAmount ?? undefined;
      // 保险起止日期回显为 range 数组
      if (data.insuranceStartDate && data.insuranceEndDate) {
        formData.insuranceDateRange = [data.insuranceStartDate, data.insuranceEndDate];
      }
      // 保险证明文件列表回显
      if (Array.isArray(data.insuranceFiles) && data.insuranceFiles.length > 0) {
        const files = data.insuranceFiles.map((f) => ({
          uid: f.id || f.fileUrl,
          name: f.fileName,
          status: "done",
          url: f.fileUrl,
        }));
        insuranceFileRef.value?.setFileList(files);
        formData.insuranceFiles = data.insuranceFiles.map((f) => ({
          fileName: f.fileName,
          fileUrl: f.fileUrl,
        }));
      }
      // 测试报告文件列表回显
      if (Array.isArray(data.testReportFiles) && data.testReportFiles.length > 0) {
        const files = data.testReportFiles.map((f) => ({
          uid: f.id || f.fileUrl,
          name: f.fileName,
          status: "done",
          url: f.fileUrl,
        }));
        testReportFileRef.value?.setFileList(files);
        formData.testReportFiles = data.testReportFiles.map((f) => ({
          fileName: f.fileName,
          fileUrl: f.fileUrl,
        }));
      }
    }
  } catch (error) {
    console.error("获取车辆详情失败:", error);
    message.error("获取车辆详情失败");
  } finally {
    submitLoading.value = false;
  }
};

// 提交
const handleSubmit = async () => {
  try {
    await formRef.value.validate();
  } catch (e) {
    message.warning("请完善必填项");
    return;
  }

  submitLoading.value = true;
  try {
    const payload = {
      ...formData,
      deptId: store.deptId,
      // 保险起止日期拆分
      insuranceStartDate: formData.insuranceDateRange?.[0] || undefined,
      insuranceEndDate: formData.insuranceDateRange?.[1] || undefined,
    };
    // 移除 range 类型字段，避免重复提交
    delete payload.insuranceDateRange;

    const res = isEdit.value
      ? await updateVehicle({ ...payload, id: vehicleId.value })
      : await addVehicle(payload);
    if (res.code === 200) {
      message.success(isEdit.value ? "变更成功" : "备案成功");
      goBack();
    } else {
      message.error(res.msg || "提交失败");
    }
  } catch (error) {
    console.error("提交失败:", error);
    message.error("提交失败");
  } finally {
    submitLoading.value = false;
  }
};

const goBack = () => {
  router.back();
};

onMounted(async () => {
  await loadVehicleTypeList();
  if (isEdit.value) {
    await loadVehicleDetail();
  }
});
</script>

<style scoped lang="less">
.vehicle-edit-page {
  display: flex;
  flex-direction: column;
  padding: 16px;
  height: calc(100vh - 70px);
  background-color: #ffffff;
  overflow: hidden;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 16px;
  flex-shrink: 0;

  .page-title {
    font-size: 18px;
    font-weight: 600;
    color: #262626;
  }
}

.page-body {
  flex: 1;
  overflow-y: auto;
}

.edit-form {
  max-width: 1000px;
  margin: 0 auto;
}

.form-section {
  margin-bottom: 32px;

  .section-title {
    font-size: 14px;
    font-weight: 600;
    color: #262626;
    margin-bottom: 16px;
    padding-left: 10px;
    border-left: 3px solid #1890ff;
    line-height: 1;
  }
}

.form-actions {
  display: flex;
  justify-content: center;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
}

.ml16 {
  margin-left: 16px;
}
</style>
